import { QuestionMarkIcon } from '@systemeio/ui/icons'
import { FormInput, Popover, toast, Tooltip } from '@systemeio/ui'
import {
  createCampaign,
  duplicateCampaign,
  removeCampaign,
  updateCampaign,
} from 'modules/campaigns/api/campaign-api'
import { getCampaignStepsListNextjsUrl } from 'modules/campaigns/campaign-steps/utils/get-campaign-steps-list-nextjs-url'
import { defaultCampaignErrors } from 'modules/campaigns/constants'
import { CampaignLoco } from 'modules/campaigns/constants/campaign-loco'
import { useCampaigns } from 'modules/campaigns/hooks/use-campaign'
import { useUpdateSenderEmailAddress } from 'modules/campaigns/hooks/use-update-sender-email-address'
import {
  CampaignCreateInterface,
  CampaignErrorsInterface,
  CampaignInterface,
  CampaignUpdateInterface,
} from 'modules/campaigns/types/campaign-interface'
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum'
import { getShareUrl } from 'modules/share/utils/get-share-url'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { CreateDataType } from 'shared/components/create-modal'
import EditModal from 'shared/components/edit-modal'
import FormTextarea from 'shared/components/form-textarea'
import LinkShareModal from 'shared/components/link-share-modal'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import SenderEmailSelect from 'shared/components/sender-email-select'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

const defaultValues: CampaignCreateInterface = {
  name: '',
  senderEmailAddress: '',
  description: '',
}

const Campaign: NextPage = () => {
  const router = useRouter()
  const { setPagination, pagination, setLimit, limit } = usePagination()
  const { user } = useUser()

  const [errors, setErrors] = useState<CampaignErrorsInterface>(defaultCampaignErrors)
  const { t } = useLocoTranslation()

  const { campaigns, mutate, counter } = useCampaigns({ limit, pagination })
  const { revalidateModules } = useRevalidateModules()

  const { updateSenderEmailAddress } = useUpdateSenderEmailAddress()

  const columns: ColumnDefinitionInterface<CampaignInterface>[] = [
    {
      key: 'name',
      header: t('global.name'),
      widthPercent: 40,
      onRender: campaign => (
        <LinkWithoutPrefetch
          className="primary-link w-full"
          href={getCampaignStepsListNextjsUrl(campaign.id)}
        >
          <Tooltip
            labelClassName={'max-w-xl'}
            mode={'overflow'}
            label={campaign.name ?? ''}
            className="truncate"
          >
            {campaign.name}
          </Tooltip>
        </LinkWithoutPrefetch>
      ),
    },
    {
      key: 'description',
      header: t('global.description'),
      widthPercent: 30,
      onRender: campaign => (
        <Tooltip
          labelClassName={'max-w-xl'}
          mode={'overflow'}
          label={campaign.description ?? ''}
          className={'truncate'}
        >
          {campaign.description}
        </Tooltip>
      ),
    },
    { key: 'stepCount', header: t('dashboard.campaign.email_count'), widthPercent: 15 },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 15,
      onRender: campaign => (
        <span>
          {dateTimeFormat(campaign.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const onRemoveCampaign = async (campaign: CampaignInterface) => {
    await removeCampaign(campaign.id)
    await revalidateModules([RequestModuleEnum.Campaign], true)
  }

  const onUpdateSenderEmailAddress = async (id: number, campaign: CampaignInterface) => {
    try {
      await updateSenderEmailAddress(id, campaign.senderEmailAddress)
      await mutate(
        data =>
          data && {
            items: data.items.map(item => {
              if (item.id === id) {
                return {
                  ...item,
                  senderEmailAddress: campaign.senderEmailAddress,
                }
              }
              return item
            }),
            hasMore: data.hasMore,
          },
        false,
      )
      await revalidateModules([RequestModuleEnum.Campaign, RequestModuleEnum.CampaignStep])
      toast.success(t('global.changes_saved'))
    } catch (e) {
      throw e
    }
  }

  const onCreateCampaign = async (data: CreateDataType<CampaignCreateInterface>) => {
    await fetchWithErrors(async () => {
      await createCampaign(data)
      await revalidateModules([RequestModuleEnum.Campaign], true)
    }, setErrors)
  }

  const onUpdateCampaign = async <T,>(
    campaign: CampaignInterface,
    newData: CampaignUpdateInterface,
  ) => {
    await fetchWithErrors(async () => {
      await updateCampaign(campaign.id, newData)
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === campaign.id) item = { ...item, ...newData }
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)

      await revalidateModules([RequestModuleEnum.Campaign, RequestModuleEnum.CampaignStep])
    }, setErrors)
  }

  const onDuplicateCampaign = async (campaign: CampaignInterface) => {
    const response = await duplicateCampaign(campaign.id)
    await revalidateModules([RequestModuleEnum.Campaign], true)
    await router.push(getCampaignStepsListNextjsUrl(response.data.id))
  }

  const onModalClose = () => setErrors(defaultCampaignErrors)

  return (
    <ListingLayout
      counter={counter}
      data={campaigns}
      title={
        <h1 className={'flex items-center gap-1.5'}>
          {t('dashboard.campaign.title_plural')}
          <Popover
            label={t('dashboard.campaign.title_tooltip')}
            popoverClassName={'w-[200px] sm:w-[400px]'}
          >
            {show => (
              <QuestionMarkIcon
                className={`${
                  show ? 'fill-brand-primary' : 'fill-fill-primary'
                } group-focus-visible:fill-brand-primary`}
              />
            )}
          </Popover>
        </h1>
      }
      type={'compact'}
      createData={{
        onCreate: data => onCreateCampaign(data),
        defaultValues: defaultValues,
        caption: t('dashboard.campaign.title'),
        onAfterLeave: onModalClose,
        requiredFields: ['name'],
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                required
                placeholder={t('global.name')}
                key="campaign-name-create"
                label={t('global.name')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.name}
              />
            ),
          },
          {
            field: 'senderEmailAddress',
            onRender: (value, onChange) => (
              <SenderEmailSelect
                required
                key="campaign-sender-email"
                onChange={data => data && onChange(data)}
                value={value}
                error={errors.senderEmailAddress}
                withoutCloseIcon
              />
            ),
          },
          {
            field: 'description',
            onRender: (value, onChange) => (
              <FormTextarea
                key="campaign-description-create"
                label={t('global.description')}
                placeholder={t('global.description')}
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                error={errors.description}
              />
            ),
          },
        ],
      }}
    >
      <Table
        data={campaigns}
        columns={columns}
        caption={t('dashboard.campaign.title')}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          settings: {
            onModalClose: onModalClose,
            onEdit: (campaign, data) => onUpdateCampaign(campaign, data),
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key="campaign-name-edit"
                    placeholder={t('global.name')}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.name}
                    label={t('global.name')}
                  />
                ),
              },
              {
                field: 'description',
                onRender: (value, onChange) => (
                  <FormTextarea
                    key="campaign-description-edit"
                    onChange={e => onChange(e.target.value)}
                    placeholder={t('global.description')}
                    value={value || ''}
                    label={t('global.description')}
                    error={errors.description}
                    className={'min-h-[126px]'}
                  />
                ),
              },
            ],
          },
          custom: [
            {
              name: 'duplicate',
              caption: t('dashboard.actions.title.duplicate'),
              onActionRender: (opened, onClose, campaign, key) => (
                <ConfirmationModal
                  confirmationText={t('dashboard.campaign.duplicate_confirm')}
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => onDuplicateCampaign(campaign)}
                  toastCaption={t('global.duplicated')}
                />
              ),
            },
            {
              name: 'updateSenderEmailAddress',
              caption: t('dashboard.campaign.action_edit_sender_email'),
              onActionRender: (opened, onClose, campaign, key) => (
                <EditModal
                  key={key}
                  instance={campaign}
                  caption={t('dashboard.campaign.edit_sender_email_title')}
                  opened={opened}
                  onClose={onClose}
                  afterLeave={onModalClose}
                  editable={[
                    {
                      field: 'senderEmailAddress',
                      disabled: value => value === null,
                      onRender: (value, onChange) => (
                        <SenderEmailSelect
                          required
                          key="campaign-sender-email"
                          onChange={data => data && onChange(data)}
                          value={value}
                          error={errors.senderEmailAddress}
                          hintLabel={t('dashboard.campaign.edit_sender_email_hint')}
                          withoutCloseIcon
                        />
                      ),
                    },
                  ]}
                  onEdit={data => onUpdateSenderEmailAddress(campaign.id, data)}
                />
              ),
            },
            {
              name: 'share',
              caption: t('dashboard.actions.title.share'),
              onActionRender: (opened, onClose, campaign, key) => (
                <LinkShareModal
                  opened={opened}
                  link={getShareUrl(ShareResourceEnum.Campaign, campaign.hash)}
                  title={t('dashboard.campaign.share_campaign')}
                  label={t('dashboard.campaign.share_campaign_text')}
                  onClose={onClose}
                  key={key}
                />
              ),
            },
          ],
          remove: {
            onChange: campaign => onRemoveCampaign(campaign),
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(CampaignLoco, 'header.top_menu.campaigns')

export default Campaign
